import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@shop/api-client'
import { formatMoney, toNumber } from './format'
import {
  Banner, Button, ErrorBanner, Field, NumberInput, Select, TextInput,
} from '@shop/ui'

export type AmendableLine = {
  lineNo: number
  accountCode: string
  debit: number
  credit: number
  memo: string | null
  partyKind?: string | null
  partyId?: string | null
}

export type AmendableJournal = {
  id: string
  journalNo: string
  entryDate: string
  memo: string | null
  lines: AmendableLine[]
}

type Account = { id: string; code: string; nameAr: string; isActive: boolean }

type Draft = {
  key: number
  accountCode: string
  debit: string
  credit: string
  memo: string | null
  partyKind?: string | null
  partyId?: string | null
}

const text = (value: number) => (value > 0 ? value.toFixed(3) : '')

/**
 * تعديلُ قيدٍ يدويّ — <b>يُعكَس القديمُ ويُرحَّل الصحيحُ في خطوةٍ واحدة</b>
 * (المالك ٢٠٢٦-١٠-٣١: «اعملي تعديل قيد»).
 *
 * ⚠ <b>وصاحبُ السطر (موظّفٌ أو مورّد) يبقى كما كان</b> — يُعدَّل الحسابُ والمبلغ،
 *   ولو ضاع الصاحبُ لَخرجت الدفعةُ من كشفه بلا أن يعرف أحد.
 */
export function JournalAmend({ journal, onDone, onCancel }: {
  journal: AmendableJournal
  onDone: () => void
  onCancel: () => void
}) {
  const queries = useQueryClient()
  const [memo, setMemo] = useState(journal.memo ?? '')
  const [date, setDate] = useState(journal.entryDate)
  const [lines, setLines] = useState<Draft[]>(() =>
    journal.lines.map((l) => ({
      key: l.lineNo,
      accountCode: l.accountCode,
      debit: text(l.debit),
      credit: text(l.credit),
      memo: l.memo,
      partyKind: l.partyKind,
      partyId: l.partyId,
    })))

  const accounts = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api<Account[]>('/api/v1/accounting/accounts'),
    staleTime: 30 * 60_000,
  })

  const debit = lines.reduce((sum, l) => sum + (toNumber(l.debit) ?? 0), 0)
  const credit = lines.reduce((sum, l) => sum + (toNumber(l.credit) ?? 0), 0)
  const balanced = Math.abs(debit - credit) < 0.0005 && debit > 0

  const save = useMutation({
    mutationFn: () =>
      api(`/api/v1/accounting/journals/${journal.id}/amend`, {
        method: 'POST',
        body: {
          memo: memo.trim(),
          entryDate: date,
          lines: lines
            .filter((l) => l.accountCode !== '' && ((toNumber(l.debit) ?? 0) > 0 || (toNumber(l.credit) ?? 0) > 0))
            .map((l) => ({
              accountCode: l.accountCode,
              debit: toNumber(l.debit) ?? 0,
              credit: toNumber(l.credit) ?? 0,
              memo: l.memo,
              partyKind: l.partyKind ?? null,
              partyId: l.partyId ?? null,
            })),
        },
      }),
    onSuccess: async () => {
      await Promise.all(
        ['journals', 'cashbook', 'trial-balance', 'income', 'balances'].map((key) =>
          queries.invalidateQueries({ queryKey: [key] })),
      )
      onDone()
    },
  })

  const change = (key: number, patch: Partial<Draft>) =>
    setLines((all) => all.map((l) => (l.key === key ? { ...l, ...patch } : l)))

  const options = (accounts.data ?? []).filter((a) => a.isActive).map((a) => (
    <option key={a.id} value={a.code}>{a.code} — {a.nameAr}</option>
  ))

  return (
    <div className="mt-2 grid gap-3 rounded-lg border border-line p-3">
      <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
        <Field label="البيان">
          <TextInput value={memo} onChange={setMemo} />
        </Field>
        <Field label="التاريخ">
          <TextInput type="date" value={date} onChange={setDate} dir="ltr" />
        </Field>
      </div>

      {lines.map((l) => (
        <div key={l.key} className="grid gap-2 sm:grid-cols-[1fr_8rem_8rem_auto] sm:items-end">
          <Field label="الحساب">
            <Select value={l.accountCode} onChange={(v) => change(l.key, { accountCode: v })}>
              <option value="">اختر…</option>
              {options}
            </Select>
          </Field>
          <Field label="مدين">
            <NumberInput value={l.debit} onChange={(v) => change(l.key, { debit: v, credit: v ? '' : l.credit })} />
          </Field>
          <Field label="دائن">
            <NumberInput value={l.credit} onChange={(v) => change(l.key, { credit: v, debit: v ? '' : l.debit })} />
          </Field>
          <Button
            disabled={lines.length <= 2}
            onClick={() => setLines((all) => all.filter((x) => x.key !== l.key))}
          >
            احذف السطر
          </Button>
        </div>
      ))}

      <div className="flex flex-wrap items-center gap-3 text-sm">
        <Button onClick={() => setLines((all) => [
          ...all,
          { key: Math.max(0, ...all.map((x) => x.key)) + 1, accountCode: '', debit: '', credit: '', memo: null },
        ])}>
          + سطر
        </Button>
        <span className="tabular" dir="ltr">{formatMoney(debit)} / {formatMoney(credit)}</span>
        {!balanced && <span className="text-danger">المدينُ يجب أن يساوي الدائن</span>}
      </div>

      <Banner tone="warn">
        القيدُ {journal.journalNo} يُعكَس، ويُرحَّل الجديدُ برقمٍ جديد — والاثنان يبقيان في «الدفاتر ← القيود».
      </Banner>

      {save.isError && <ErrorBanner error={save.error} />}

      <div className="flex gap-2">
        <Button
          tone="primary"
          disabled={!balanced || memo.trim() === '' || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'يُحفظ…' : 'احفظ التعديل'}
        </Button>
        <Button onClick={onCancel}>تراجع</Button>
      </div>
    </div>
  )
}
